import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useTheme } from "../hooks/usePersistentToggle";
import {
  HomeIcon,
  ProductIcon,
  ContainerIcon,
  LocationIcon,
  QRCodeIcon,
  GridViewIcon,
  TrashIcon,
  OrganizeIcon,
  CameraIcon,
  PlusIcon,
  SettingsIcon,
  UserIcon,
  EmailIcon,
  ShoppingBagIcon,
  ClipboardListIcon,
  TrendingUpIcon,
  BoxIcon,
  SunIcon,
  MoonIcon,
  SidebarIcon,
} from "./Icons";

const COUNTS_REFRESH_TTL_MS = 30000;

// Sections render in order; `count` names a key of AppContext counts
const NAV_SECTIONS = [
  {
    items: [{ path: "/", label: "Dashboard", Icon: HomeIcon }],
  },
  {
    label: "Inventory",
    items: [
      { path: "/products", label: "Products", Icon: ProductIcon, count: "products" },
      { path: "/board", label: "Board", Icon: GridViewIcon },
      { path: "/containers", label: "Containers", Icon: ContainerIcon, count: "containers" },
      { path: "/locations", label: "Locations", Icon: LocationIcon, count: "locations" },
      { path: "/views", label: "Custom Views", Icon: GridViewIcon },
      { path: "/organize", label: "Organize", Icon: OrganizeIcon },
      { path: "/trash", label: "Trash", Icon: TrashIcon, count: "trash" },
    ],
  },
  {
    label: "Actions",
    items: [
      { path: "/create", label: "Add New Item", Icon: PlusIcon },
      { path: "/scan", label: "Scan QR Code", Icon: CameraIcon },
      { path: "/qr-codes", label: "Generate QR Codes", Icon: QRCodeIcon },
      { path: "/email-scanning", label: "Email Scanning", Icon: EmailIcon },
    ],
  },
  {
    label: "Sales",
    items: [
      { path: "/sales/sold", label: "Items Sold", Icon: ShoppingBagIcon },
      { path: "/sales/listed", label: "Items Listed", Icon: ClipboardListIcon },
      { path: "/sales/analytics", label: "Analytics", Icon: TrendingUpIcon },
    ],
  },
];

function NavItem({ label, Icon, active, badge, collapsed, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`sidebar-nav-item ${active ? "active" : ""}`}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      aria-current={active ? "page" : undefined}
    >
      <span className="sidebar-nav-item-icon">
        <Icon size={18} />
      </span>
      <span className="sidebar-nav-item-label">{label}</span>
      {badge !== undefined && <span className="sidebar-badge">{badge}</span>}
    </button>
  );
}

function Sidebar({ collapsed, onToggleCollapsed }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { counts, refreshCounts } = useApp();
  const [theme, toggleTheme] = useTheme();
  const lastCountsRefresh = useRef(0);

  // Refresh counts on mount, then at most every 30s on route changes;
  // mutating actions refresh them directly via AppContext
  useEffect(() => {
    const now = Date.now();
    if (now - lastCountsRefresh.current < COUNTS_REFRESH_TTL_MS) return;
    lastCountsRefresh.current = now;
    refreshCounts();
  }, [location.pathname, refreshCounts]);

  const isActive = (path) => location.pathname === path;

  const item = ({ path, label, Icon, count }) => (
    <NavItem
      key={path}
      label={label}
      Icon={Icon}
      active={isActive(path)}
      badge={count ? counts[count] : undefined}
      collapsed={collapsed}
      onClick={() => navigate(path)}
    />
  );

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <BoxIcon size={20} />
        </div>
        <div className="sidebar-brand">
          FrugalStack
          <small>Resale inventory</small>
        </div>
      </div>

      <div className="sidebar-content">
        {NAV_SECTIONS.map((section, i) => (
          <React.Fragment key={section.label || i}>
            {section.label && (
              <div className="sidebar-section-label">{section.label}</div>
            )}
            <nav className="sidebar-nav">{section.items.map(item)}</nav>
          </React.Fragment>
        ))}

        <div className="sidebar-footer">
          {item({ path: "/settings", label: "Settings", Icon: SettingsIcon })}
          {item({ path: "/account", label: "Account", Icon: UserIcon })}
          <NavItem
            label={theme === "dark" ? "Light mode" : "Dark mode"}
            Icon={theme === "dark" ? SunIcon : MoonIcon}
            collapsed={collapsed}
            onClick={toggleTheme}
          />
          <NavItem
            label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            Icon={SidebarIcon}
            collapsed={collapsed}
            onClick={onToggleCollapsed}
          />
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
